import { CfsTemplateError, CfsTemplateErrorData } from "../../models";
import {
  VideoPlayerErrorMessages,
  VideoPlayerErrorTypes,
  VimeoPlayerErrorMessages,
  YoutubePlayerErrorMessages
} from "./video-template/video-player-error-messages";

export enum CfsTemplateErrorMessages {
  Unknown = "Unknown error",
  ImageLoad = "Failed to load image",
  VideoLoad = "Failed to load video",
  JsonParse = "JSON is invalid. Failed to parse JSON",
  EmptySrc = "Media source is empty",
  BrowserCompatibility = "Browser compatibility error",
  TooLongLoading = "Template is taking too long to load",
  EmptyData = "Template data is empty"
}

export enum CfsTemplateErrorTypes {
  ImageLoad = "ImageLoadError",
  VideoLoad = "VideoLoadError",
  JsonParse = "InvalidJsonError",
  EmptySrc = "EmptySrcError",
  EmptyData = "EmptyDataError",
  BrowserCompatibility = "BrowserCompatibilityError",
  FeatureTemporarilyDisabled = "FeatureTemporarilyDisabled",
  TooLongLoading = "TooLongLoadingError",
  Unknown = "UnknownError"
}

export enum CfsTemplateErrorTypeTexts {
  ImageLoad = "Image load error",
  VideoLoad = "Video load error",
  JsonParse = "Invalid JSON error",
  EmptySrc = "Empty src error",
  EmptyData = "Empty data error",
  Unknown = "Unknown error"
}

export enum CfsHtmlTemplateErrorMessages {
  LogoLoad = "Failed to load logo",
  QrCodeLoad = "Failed to load QR code",
  MainImageLoad = "Failed to load main image",
  FontLoad = "Font failed to load"
}

export enum CfsHtmlTemplateErrorTypes {
  LogoLoad = "LogoLoadError",
  QrCodeLoad = "QrCodeLoadError",
  MainImageLoad = "MainImageLoadError",
  FontLoad = "FontLoadError"
}

export enum CfsHtmlTemplateErrorTypeTexts {
  LogoLoad = "Logo load error",
  QrCodeLoad = "QR code load error",
  MainImageLoad = "Main image load error",
  FontLoad = "Font load error"
}

export enum CfsTemplateErrorDataFields {
  Url = "url",
  FontName = "fontName",
  Id = "id"
}

export const generateCfsHtmlTemplateErrorMessage = (type: CfsHtmlTemplateErrorTypes | CfsTemplateErrorTypes): string => {
  switch (type) {
    case CfsHtmlTemplateErrorTypes.LogoLoad:
      return CfsHtmlTemplateErrorMessages.LogoLoad;
    case CfsHtmlTemplateErrorTypes.QrCodeLoad:
      return CfsHtmlTemplateErrorMessages.QrCodeLoad;
    case CfsHtmlTemplateErrorTypes.MainImageLoad:
      return CfsHtmlTemplateErrorMessages.MainImageLoad;
    case CfsHtmlTemplateErrorTypes.FontLoad:
      return CfsHtmlTemplateErrorMessages.FontLoad;
    case CfsTemplateErrorTypes.EmptyData:
      return CfsTemplateErrorMessages.EmptyData;
    default:
      return CfsTemplateErrorMessages.Unknown;
  }
};

export const generateCfsTemplateErrorMessage = (type: CfsTemplateErrorTypes): string => {
  switch (type) {
    case CfsTemplateErrorTypes.ImageLoad:
      return CfsTemplateErrorMessages.ImageLoad;
    case CfsTemplateErrorTypes.VideoLoad:
      return CfsTemplateErrorMessages.VideoLoad;
    case CfsTemplateErrorTypes.JsonParse:
      return CfsTemplateErrorMessages.JsonParse;
    case CfsTemplateErrorTypes.EmptySrc:
      return CfsTemplateErrorMessages.EmptySrc;
    case CfsTemplateErrorTypes.EmptyData:
      return CfsTemplateErrorMessages.EmptyData;
    case CfsTemplateErrorTypes.TooLongLoading:
      return CfsTemplateErrorMessages.TooLongLoading;
    default:
      return CfsTemplateErrorMessages.Unknown;
  }
};

export const generateVimeoPlayerErrorMessage = (type: VideoPlayerErrorTypes | string): string => {
  switch (type) {
    case VideoPlayerErrorTypes.RequiredId:
      return VimeoPlayerErrorMessages.RequiredId;
    case VideoPlayerErrorTypes.InvalidUrl:
      return VimeoPlayerErrorMessages.InvalidUrl;
    case VideoPlayerErrorTypes.Play:
      return VideoPlayerErrorMessages.Play;
    case VideoPlayerErrorTypes.Load:
      return VideoPlayerErrorMessages.Load;
    default:
      return CfsTemplateErrorMessages.Unknown;
  }
};

export const generateYoutubePlayerErrorMessage = (type: VideoPlayerErrorTypes | string): string => {
  switch (type) {
    case VideoPlayerErrorTypes.RequiredId:
      return YoutubePlayerErrorMessages.RequiredId;
    case VideoPlayerErrorTypes.InvalidUrl:
      return YoutubePlayerErrorMessages.InvalidUrl;
    case VideoPlayerErrorTypes.Play:
      return VideoPlayerErrorMessages.Play;
    case VideoPlayerErrorTypes.Load:
      return VideoPlayerErrorMessages.Load;
    default:
      return CfsTemplateErrorMessages.Unknown;
  }
};

export const generateCommonCfsTemplateError = (
  type: CfsTemplateErrorTypes,
  message = "",
  data: CfsTemplateErrorData | null = null
): CfsTemplateError => {
  if (!message) {
    message = generateCfsTemplateErrorMessage(type);
  }

  return generateCfsTemplateError(type, message, data);
};

export const generateCfsTemplateError = (type: string, message: string, data: CfsTemplateErrorData | null = null): CfsTemplateError => {
  const date = new Date().toISOString();

  return {
    type,
    message,
    data,
    date
  };
};

export const generateErrorTypeText = (error: CfsTemplateError, includeUnknown = false): string => {
  const type: CfsHtmlTemplateErrorTypes | CfsTemplateErrorTypes | string =
    error.type || (includeUnknown ? CfsTemplateErrorTypes.Unknown : "");

  switch (type) {
    case CfsTemplateErrorTypes.ImageLoad:
      return CfsTemplateErrorTypeTexts.ImageLoad;
    case CfsTemplateErrorTypes.VideoLoad:
      return CfsTemplateErrorTypeTexts.VideoLoad;
    case CfsTemplateErrorTypes.JsonParse:
      return CfsTemplateErrorTypeTexts.JsonParse;
    case CfsTemplateErrorTypes.EmptySrc:
      return CfsTemplateErrorTypeTexts.EmptySrc;
    case CfsTemplateErrorTypes.EmptyData:
      return CfsTemplateErrorTypeTexts.EmptyData;
    case CfsHtmlTemplateErrorTypes.LogoLoad:
      return CfsHtmlTemplateErrorTypeTexts.LogoLoad;
    case CfsHtmlTemplateErrorTypes.QrCodeLoad:
      return CfsHtmlTemplateErrorTypeTexts.QrCodeLoad;
    case CfsHtmlTemplateErrorTypes.MainImageLoad:
      return CfsHtmlTemplateErrorTypeTexts.MainImageLoad;
    case CfsHtmlTemplateErrorTypes.FontLoad:
      return CfsHtmlTemplateErrorTypeTexts.FontLoad;
  }

  return includeUnknown ? CfsTemplateErrorTypeTexts.Unknown : "";
};
